import PropTypes from "prop-types";
import React, { useEffect, useState, useRef } from "react";

const Users = (props) => {
  const { course, allUsers, courses, setCourses } = props;

  const [availableUsers, setAvailableUsers] = useState(
    allUsers.filter((course) => !course.users?.some((c) => c.id === course.id)),
  );
  const [users, setUsers] = useState(course.users || []);
  const [openUsersDropdown, setOpenUsersDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const dropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenUsersDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setUsers(course.users || []);
  }, [course]);

  const patchClient = async (params) => {
    return await fetch("client_admin/courses/" + course.id + "/users", {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
      },
      body: JSON.stringify({
        course: params,
      }),
    });
  };

  const handleCertificateDownload = async (user) => {
    try {
      const response = await fetch(
        `/client_admin/certificates/${course.id}?user_id=${user.id}`,
        {
          method: "GET",
          headers: {
            Accept: "application/pdf",
            "X-CSRF-Token": document.querySelector('[name="csrf-token"]')
              .content,
          },
        },
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${user.name} ${course.title} certyfikat.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        console.error("Failed to download certificate");
      }
    } catch (error) {
      console.error("Error downloading certificate:", error);
    }
  };

  const handleNewUser = async (userId) => {
    const response = await patchClient({
      user_ids: [...users.map((c) => c.id), userId],
    });

    if (response.ok) {
      const data = await response.json();
      setUsers(data.course.users);
      setCourses(
        courses.map((oldCourse) =>
          oldCourse.id == course.id ? data.course : oldCourse,
        ),
      );

      setOpenUsersDropdown(false);
      setSearchTerm(""); // Clear search when course is added
    } else {
      console.log("error");
    }
  };

  const handleDeleteUser = async (userId) => {
    const response = await patchClient({
      user_ids: users.filter((u) => u.id !== userId).map((u) => u.id),
    });

    if (response.ok) {
      const data = await response.json();
      setUsers(data.course.users);
      setCourses(
        courses.map((oldCourse) =>
          oldCourse.id == course.id ? data.course : oldCourse,
        ),
      );

      setOpenUsersDropdown(false);
    } else {
      console.log("error");
    }
  };

  useEffect(() => {
    setAvailableUsers(
      allUsers.filter((course) => !users.some((c) => c.id === course.id)),
    );
  }, [users]);

  // Filter available users based on search term
  const filteredUsers = availableUsers.filter((user) =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleDropdownToggle = () => {
    setOpenUsersDropdown(!openUsersDropdown);
    if (!openUsersDropdown) {
      setSearchTerm(""); // Clear search when opening dropdown
    }
  };

  const usersTable = users.map((user) => {
    return (
      <div key={user.id} className="bg-gray-50 p-4 mb-3">
        <div className="flex flex-col w-full">
          <div className="font-medium text-lg mb-1 underline">{user.name}</div>
          <div className="flex justify-between items-start gap-4">
            <div className="w-full">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm">
                  {user.progress == 100 ? "Ukończone" : "W trakcie"}
                </span>
                <span className="text-xs text-gray-400">{user.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
                <div
                  className="bg-black h-2 rounded-full transition-all duration-300 ease-in-out"
                  style={{ width: `${user.progress}%` }}
                ></div>
              </div>
            </div>

            <div className="text-right text-sm text-gray-600">
              <button
                className="flex w-max items-center gap-2
                  px-6 py-2 bg-white text-primaryYellow border border-primaryYellow text-sm
                  hover:bg-black hover:text-white
                  enabled:cursor-pointer
                  transition-colors
                "
                onClick={() => handleDeleteUser(user.id)}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3H5m14 0H5"
                  />
                </svg>
                Usuń dostęp
              </button>
            </div>
          </div>

          {user.progress == 100 && (
            <div className="text-center">
              <button
                onClick={() => handleCertificateDownload(user)}
                className="flex w-max items-center gap-2
                   text-primaryYellow text-sm
                  hover:text-black
                  enabled:cursor-pointer
                  transition-colors underline
                "
              >
                Pobierz certyfikat ukończenia
              </button>
            </div>
          )}
        </div>
      </div>
    );
  });

  return (
    <>
      <div className="border-b">
        {availableUsers.length ? (
          <button
            onClick={handleDropdownToggle}
            className="flex justify-between w-full border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium hover:bg-gray-50"
          >
            <span className="text-sm font-medium">
              {availableUsers[0].name}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-light">dodaj</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m19.5 8.25-7.5 7.5-7.5-7.5"
                />
              </svg>
            </div>
          </button>
        ) : (
          <></>
        )}
      </div>

      <div className="relative">
        {openUsersDropdown && (
          <div
            className="absolute w-full mt-1 border bg-white shadow-lg z-50"
            ref={dropdownRef}
          >
            {/* Search input */}
            <div className="p-3 border-b border-gray-200">
              <input
                type="text"
                placeholder="Szukaj szkoleń..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>

            {/* Users list */}
            <div className="max-h-60 overflow-auto">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <button
                    key={user.id}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                    onClick={() => handleNewUser(user.id)}
                  >
                    {user.name}
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500">
                  Nie znaleziono Użytkowników
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-5">{usersTable}</div>
      </div>
    </>
  );
};

export default Users;

Users.propTypes = {
  course: PropTypes.object,
  allUsers: PropTypes.array,
  courses: PropTypes.array,
  setCourses: PropTypes.func,
};
